import * as React from 'react';
import { toast } from '@grandvisionhq/common';
import { DiscountCodeForm } from '@grandvisionhq/forms';
import { useMutation } from '@grandvisionhq/graphql';
import { useFeatureToggles, useIntl } from '@grandvisionhq/state';
import { useDispatchTrackEvent } from '@grandvisionhq/svc-tealium/events';
import { isNil } from '@grandvisionhq/utils';
import '../../../operations/fragments/cart-details.js';
import '../../../operations/fragments/cart-validation-errors.js';
import '../../../operations/fragments/recover-inactive-cart-failure.js';
import '../../../operations/fragments/recover-inactive-cart-result.js';
import '../../../operations/mutations/add-cart-line-items.js';
import { ADD_CART_VOUCHER_CODES } from '../../../operations/mutations/add-cart-voucher-codes.js';
import '../../../operations/mutations/create-cart.js';
import '../../../operations/mutations/create-cart-from-instant-checkout-link.js';
import '../../../operations/mutations/create-cart-from-order.js';
import '../../../operations/mutations/duplicate-cart-line-items.js';
import '../../../operations/mutations/remove-cart-voucher-codes.js';
import '../../../operations/mutations/remove-cart-line-items.js';
import '../../../operations/mutations/update-cart-line-items.js';
import '../../../operations/mutations/recover-inactive-cart.js';
import { GET_CART } from '../../../operations/queries/get-cart.js';
import '../../../operations/queries/get-total-line-items.js';
import '../../../operations/queries/get-cart-estimated-delivery.js';
import { getUnappliedVoucherCodes } from '../../../utils/get-unapplied-voucher-codes.js';

const VoucherCodeFormContainer = ({ inputId , className , 'data-t': dataT , cartId ,  })=>{
    const { isOmniVoucherEnabled  } = useFeatureToggles();
    const { getLabel  } = useIntl();
    const voucherCodeInputRef = React.useRef('');
    const dispatchTrackEvent = useDispatchTrackEvent();
    const [shouldClearDiscountCode, setShouldClearDiscountCode] = React.useState(false);
    const handleError = React.useCallback((e)=>{
        console.error('Voucher error', e);
        toast.failed('common.error.network');
    }, []);
    const handleVoucherSuccessTealium = React.useCallback((discountCode, discountName)=>{
        dispatchTrackEvent(()=>Promise.resolve({
                id: 'Event',
                Events_DiscountApplied: '1',
                Order_DiscountName: discountName,
                Order_DiscountCode: discountCode
            })
        );
    }, [
        dispatchTrackEvent
    ]);
    const handleTentativeVoucherSuccessTealium = React.useCallback((discountCode, errorMessage)=>{
        dispatchTrackEvent(()=>Promise.resolve({
                id: 'Event',
                Events_DiscountTentative: '1',
                Error_Source: 'User',
                Error_Code: `PromoCode - ${errorMessage}`,
                Order_DiscountCode: discountCode
            })
        );
    }, [
        dispatchTrackEvent
    ]);
    const [addCartVoucherCodes] = useMutation(ADD_CART_VOUCHER_CODES, {
        onError: handleError,
        onCompleted (data) {
            var ref, ref1, ref2, ref3;
            const newCart = data === null || data === void 0 ? void 0 : data.addCartVoucherCodes.cart;
            const voucherStates = newCart === null || newCart === void 0 ? void 0 : (ref = newCart.vouchers) === null || ref === void 0 ? void 0 : ref.map(({ state  })=>state
            );
            // First check if any voucher codes are not in the state of "MatchesCart"
            // according to: https://docs.commercetools.com/api/projects/carts#discountcodestate
            // Show a warning toast for all voucher states other than MatchesCart
            if (voucherStates === null || voucherStates === void 0 ? void 0 : voucherStates.some((state)=>state != 'MatchesCart'
            )) {
                var ref4;
                newCart === null || newCart === void 0 ? void 0 : (ref4 = newCart.vouchers) === null || ref4 === void 0 ? void 0 : ref4.forEach(({ state , code  })=>{
                    if (state !== 'MatchesCart') {
                        if (voucherCodeInputRef.current) {
                            handleTentativeVoucherSuccessTealium(code, getLabel(`cart.validations.discountcode.${state.toLowerCase()}`));
                        }
                        toast.failed(`cart.validations.discountcode.${state.toLowerCase()}`);
                    }
                });
                return;
            }
            // Check if some vouchers are not applicable to the cart and show toast message
            if (newCart === null || newCart === void 0 ? void 0 : (ref1 = newCart.vouchers) === null || ref1 === void 0 ? void 0 : ref1.length) {
                const unappliedVouchers = getUnappliedVoucherCodes(newCart === null || newCart === void 0 ? void 0 : newCart.vouchers, newCart === null || newCart === void 0 ? void 0 : newCart.appliedDiscounts);
                const currentVoucherInApplied = unappliedVouchers === null || unappliedVouchers === void 0 ? void 0 : unappliedVouchers.find((voucher)=>voucher.code == voucherCodeInputRef.current
                );
                if (currentVoucherInApplied) {
                    handleVoucherSuccessTealium(currentVoucherInApplied.code, currentVoucherInApplied.name);
                }
                if (unappliedVouchers === null || unappliedVouchers === void 0 ? void 0 : unappliedVouchers.length) {
                    toast.failed(`cart.validations.discountcode.doesnotmatchcart`);
                    return;
                }
            }
            if ((ref2 = data === null || data === void 0 ? void 0 : data.addCartVoucherCodes.failures) === null || ref2 === void 0 ? void 0 : ref2.length) {
                for (const failure of data.addCartVoucherCodes.failures){
                    if (failure.__typename === 'VoucherCodeNotApplicableFailure') {
                        handleTentativeVoucherSuccessTealium(voucherCodeInputRef.current, getLabel('cart.validations.discountcode.notvalid'));
                        toast.failed('cart.validations.discountcode.notvalid');
                        return;
                    } else if (failure.__typename === 'UnknownCartMutationFailure') {
                        handleTentativeVoucherSuccessTealium(voucherCodeInputRef.current, getLabel('common.error.network'));
                        toast.failed('common.error.network');
                        return;
                    }
                }
            }
            const appliedVoucher = newCart === null || newCart === void 0 ? void 0 : (ref3 = newCart.vouchers) === null || ref3 === void 0 ? void 0 : ref3.find((voucher)=>voucher.code === voucherCodeInputRef.current
            );
            if (appliedVoucher) {
                handleVoucherSuccessTealium(appliedVoucher.code, appliedVoucher.name);
            }
            // If no problems fire success toast
            toast.success('cart.validations.discountcode.matchescart');
            setShouldClearDiscountCode(true);
        }
    });
    const handleSubmit = React.useCallback((inputValue)=>{
        setShouldClearDiscountCode(false);
        if (!cartId) {
            return;
        }
        voucherCodeInputRef.current = inputValue;
        addCartVoucherCodes({
            variables: {
                cartInput: {
                    cartId,
                    voucherCodes: [
                        inputValue
                    ]
                }
            },
            update (cache, { data  }) {
                if (data && data.addCartVoucherCodes.success === true) {
                    const newCart = data.addCartVoucherCodes.cart;
                    cache.writeQuery({
                        query: GET_CART,
                        variables: {
                            id: cartId
                        },
                        data: {
                            cart: {
                                cart: newCart,
                                validationErrors: null,
                                failures: null
                            }
                        }
                    });
                }
            }
        });
    }, [
        cartId,
        addCartVoucherCodes
    ]);
    if (isNil(cartId)) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(DiscountCodeForm, {
        content: {
            inputField: {
                inputId: inputId || 'discount-code',
                type: 'text'
            }
        },
        className: className,
        onSubmit: handleSubmit,
        placeholderLabel: getLabel('cart.discountForm.placeholder'),
        buttonCaptionLabel: getLabel('cart.discountForm.cta'),
        "data-t": dataT,
        showVoucherScanner: isOmniVoucherEnabled,
        shouldClearDiscountCode: shouldClearDiscountCode
    });
};

export { VoucherCodeFormContainer };
//# sourceMappingURL=VoucherCodeFormContainer.js.map
